import { FC, useMemo } from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './TablePagination.scss';

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

const ELLIPSIS = 'ellipsis';
type PageItem = number | typeof ELLIPSIS;

// First and last page, the current page and its neighbours, with "…" for the gaps
export const getPageItems = (page: number, totalPages: number): PageItem[] => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const items: PageItem[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) items.push(ELLIPSIS);
  for (let current = start; current <= end; current += 1) items.push(current);
  if (end < totalPages - 1) items.push(ELLIPSIS);
  items.push(totalPages);
  return items;
};

interface TablePaginationProps {
  /** 1-based current page */
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /** Shows the "Show [n]" page-size select when given */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  disabled?: boolean;
}

const TablePagination: FC<TablePaginationProps> = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  disabled = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageItems = useMemo(() => getPageItems(page, totalPages), [page, totalPages]);

  const goTo = (nextPage: number) => {
    if (!disabled && nextPage !== page && nextPage >= 1 && nextPage <= totalPages) onPageChange(nextPage);
  };

  return (
    <div className='tablePagination'>
      {onPageSizeChange ? (
        <div className='tablePaginationSize'>
          <span>Show</span>
          <Select
            size='small'
            value={pageSize}
            disabled={disabled}
            onChange={(event: SelectChangeEvent<number>) => onPageSizeChange(Number(event.target.value))}
            inputProps={{ 'aria-label': 'Rows per page' }}
            className='tablePaginationSelect'
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </div>
      ) : (
        <span />
      )}
      <nav className='tablePaginationPages' aria-label='Pagination'>
        <button
          type='button'
          className='tablePaginationArrow'
          onClick={() => goTo(page - 1)}
          disabled={disabled || page <= 1}
          aria-label='Previous page'
        >
          <ChevronLeftIcon fontSize='small' />
        </button>
        {pageItems.map((item, index) =>
          item === ELLIPSIS ? (
            <span key={`ellipsis-${index}`} className='tablePaginationEllipsis'>
              …
            </span>
          ) : (
            <button
              type='button'
              key={item}
              className={`tablePaginationPage${item === page ? ' tablePaginationPage--active' : ''}`}
              onClick={() => goTo(item)}
              disabled={disabled}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          ),
        )}
        <button
          type='button'
          className='tablePaginationArrow'
          onClick={() => goTo(page + 1)}
          disabled={disabled || page >= totalPages}
          aria-label='Next page'
        >
          <ChevronRightIcon fontSize='small' />
        </button>
      </nav>
    </div>
  );
};

export default TablePagination;
